import { addComponent, defineQuery, hasComponent, removeComponent } from "bitecs";
import { anyEntityWith } from "../utils/bit-utils";
import {
  HeldRemoteLeft,
  HeldRemoteRight,
  Hovered,
  HoveredRemoteLeft,
  HoveredRemoteRight,
  NotRemoteHoverTarget,
  RemoteHoverTarget
} from "../bit-components";
import { paths } from "../systems/userinput/paths";
import { sets } from "../systems/userinput/sets";
import { getLastWorldPosition } from "../utils/three-utils";
import { Layers } from "../camera-layers";
import configManager from "../singleton/ConfigManager";

export function findRemoteHoverTarget(world, object3D) {
  if (!object3D) return null;
  if (!object3D.eid) return findRemoteHoverTarget(world, object3D.parent);
  if (hasComponent(world, NotRemoteHoverTarget, object3D.eid)) return null;
  if (hasComponent(world, RemoteHoverTarget, object3D.eid)) return object3D.eid;
  return findRemoteHoverTarget(world, object3D.parent);
}

const hoveredRightRemoteQuery = defineQuery([HoveredRemoteRight]);
const hoveredLeftRemoteQuery = defineQuery([HoveredRemoteLeft]);
const hoveredQuery = defineQuery([Hovered]);

const HIGHLIGHT = new THREE.Color(22 / 255, 181 / 255, 253 / 255);
const NO_HIGHLIGHT = new THREE.Color(190 / 255, 190 / 255, 190 / 255);
const TRANSFORM_COLOR_1 = new THREE.Color(22 / 255, 181 / 255, 253 / 255);
const TRANSFORM_COLOR_2 = new THREE.Color(22 / 255, 181 / 255, 253 / 255);

AFRAME.registerComponent("cursor-controller", {
  schema: {
    cursor: { type: "selector" },
    camera: { type: "selector" },
    far: { default: 100 },
    near: { default: 0.01 },
    defaultDistance: { default: 4 },
    minDistance: { default: 0.18 },
    inactivityTimeout: { default: 3000 } // 3 seconds in milliseconds
  },

  init: function () {
    this.enabled = false;

    this.cursorVisual = new THREE.Mesh(
      new THREE.PlaneGeometry(),
      new THREE.ShaderMaterial({
        depthTest: false,
        uniforms: {
          color: { value: new THREE.Color(0xfe12a8) }
        },
        vertexShader: `
          varying vec2 vPos;
          void main() {
            vPos = position.xy;

            vec4 mvPosition = modelViewMatrix * vec4( 0.0, 0.0, 0.0, 1.0 );

            vec2 scale = vec2(
              length( vec3( modelMatrix[ 0 ].x, modelMatrix[ 0 ].y, modelMatrix[ 0 ].z ) ),
              length( vec3( modelMatrix[ 1 ].x, modelMatrix[ 1 ].y, modelMatrix[ 1 ].z ) )
            );

            float distance = -mvPosition.z;
            scale *= distance; // negates projection scale
            scale += min(1.0/distance, 0.3); // scale in screen space

            float radius = 0.02;
            mvPosition.xy += position.xy * radius * scale;
            gl_Position = projectionMatrix * mvPosition;
          }`,
        fragmentShader: `
          uniform vec3 color;
          varying vec2 vPos;

          void main() {
            float distance = length(vPos);
            if (distance > 0.5) {
                discard;
            }

            gl_FragColor = vec4(
              mix(color, vec3(0.0), step(0.35, distance)),
              0.8
            );

            // #include <tonemapping_fragment>
            #include <colorspace_fragment>
          }`
      })
    );

    const setCursorScale = () => {
      this.cursorVisual.scale.setScalar(APP.store.state.preferences["cursorSize"] || 1);
      this.cursorVisual.matrixNeedsUpdate = true;
    };
    APP.store.addEventListener("statechanged", setCursorScale);
    setCursorScale();

    this.cursorVisual.renderOrder = window.APP.RENDER_ORDER.CURSOR;
    this.cursorVisual.material.transparent = true;
    this.cursorVisual.layers.set(Layers.CAMERA_LAYER_UI);
    this.cursorVisual.layers.enable(Layers.CAMERA_LAYER_FX_MASK);
    this.data.cursor.object3D.add(this.cursorVisual);

    this.intersection = null;
    this.raycaster = new THREE.Raycaster();
    this.raycaster.firstHitOnly = true; // flag specific to three-mesh-bvh
    this.distance = this.data.far;
    this.color = this.cursorVisual.material.uniforms.color.value;

    const lineGeometry = new THREE.BufferGeometry();
    lineGeometry.setAttribute("position", new THREE.BufferAttribute(new Float32Array(2 * 3), 3));

    this.line = new THREE.Line(
      lineGeometry,
      new THREE.LineBasicMaterial({
        color: "white",
        opacity: 0.2,
        transparent: true,
        visible: false
      })
    );
    this.el.setObject3D("line", this.line);

    // --- New properties for inactivity ---
    this.isIdle = false;
    this.lastMoveTime = performance.now();
    this.lastPosition = new THREE.Vector3();
    this.lastDirection = new THREE.Vector3();
    // Thresholds to avoid hiding due to micro-jitters
    this.positionThresholdSq = 0.0001 * 0.0001; // Squared distance threshold (e.g., 0.1mm)
    this.directionThreshold = Math.cos((0.1 * Math.PI) / 180); // Cosine of the angle threshold (e.g., 0.1 degrees)
  },

  update: function () {
    this.raycaster.far = this.data.far;
    this.raycaster.near = this.data.near;
  },

  tick2: (() => {
    const rawIntersections = [];
    const cameraPos = new THREE.Vector3();
    const v = new THREE.Vector3();

    const isPointerVisible = pointer => {
      return pointer.visible && pointer.parent.visible;
    };

    const currentPosition = new THREE.Vector3();
    const currentDirection = new THREE.Vector3();

    return function (t, left) {
      const userinput = AFRAME.scenes[0].systems.userinput;
      const cursorPose = userinput.get(left ? paths.actions.cursor.left.pose : paths.actions.cursor.right.pose);
      const systemEnabled = this.enabled && !!cursorPose && configManager.config.cursorVisible.enabled;

      // --- Inactivity Check Logic ---
      let moved = false;
      if (systemEnabled) {
        // Get current pose safely
        currentPosition.copy(cursorPose.position);
        currentDirection.copy(cursorPose.direction);

        // Check for significant movement only if we have a previous pose
        if (this.lastMoveTime > 0) {
          // Ensure we've had at least one frame
          const positionChanged = currentPosition.distanceToSquared(this.lastPosition) > this.positionThresholdSq;
          const directionChanged = currentDirection.dot(this.lastDirection) < this.directionThreshold;
          moved = positionChanged || directionChanged;
        } else {
          moved = true; // Consider the first frame as movement
        }

        if (moved) {
          this.lastMoveTime = performance.now();
          this.isIdle = false;
          // Update last known pose
          this.lastPosition.copy(currentPosition);
          this.lastDirection.copy(currentDirection);
        } else {
          // Check if idle timeout exceeded only if not already idle
          if (!this.isIdle && performance.now() - this.lastMoveTime > this.data.inactivityTimeout) {
            this.isIdle = true;
          }
        }
      } else {
        // If the system is disabled or pose is lost, reset idle state and timer
        this.isIdle = false;
        this.lastMoveTime = performance.now();
        // Reset last pose comparison values
        this.lastPosition.set(0, 0, 0); // Or some other default
        this.lastDirection.set(0, 0, -1);
      }

      const handPointers = window.APP.handPointers;
      let pointer = left ? handPointers.find(p => p.name === "left") : handPointers.find(p => p.name === "right");
      if (!pointer) pointer = left ? handPointers[0] : handPointers[1];
      if (pointer.cursorObject) pointer.cursorObject.visible = false;

      const overallDisabled = !systemEnabled;
      pointer.visible = !overallDisabled;

      const hideLine =
        userinput.get(left ? paths.actions.cursor.left.hideLine : paths.actions.cursor.right.hideLine) ||
        isPointerVisible(pointer);

      // --- Modify Visibility based on Idle State ---
      const shouldBeVisible = systemEnabled && !this.isIdle;
      this.data.cursor.object3D.visible = shouldBeVisible;
      this.line.material.visible = shouldBeVisible && !hideLine; // Also check !hideLine

      this.intersection = null;

      // --- Early exit if not visible/enabled ---
      if (!shouldBeVisible) {
        const hovered = left ? hoveredLeftRemoteQuery(APP.world) : hoveredRightRemoteQuery(APP.world);
        for (let i = 0; i < hovered.length; i++) {
          removeComponent(APP.world, left ? HoveredRemoteLeft : HoveredRemoteRight, hovered[i]);
          removeComponent(APP.world, Hovered, hovered[i]);
        }
        return;
      }

      this.el.sceneEl.systems["hubs-systems"].characterController.avatarPOV.object3D.updateMatrices();
      const playerScale = v
        .setFromMatrixColumn(
          this.el.sceneEl.systems["hubs-systems"].characterController.avatarPOV.object3D.matrixWorld,
          1
        )
        .length();
      this.raycaster.far = this.data.far * playerScale;
      this.raycaster.near = this.data.near * playerScale;

      const isGrabbing = left ? anyEntityWith(APP.world, HeldRemoteLeft) : anyEntityWith(APP.world, HeldRemoteRight);
      let isHoveringSomething = false;
      rawIntersections.length = 0;
      this.raycaster.ray.origin.copy(cursorPose.position); // Use cursorPose directly
      this.raycaster.ray.direction.copy(cursorPose.direction); // Use cursorPose directly
      this.raycaster.intersectObjects(
        AFRAME.scenes[0].systems["hubs-systems"].cursorTargettingSystem.targets,
        true,
        rawIntersections
      );
      this.intersection = rawIntersections[0];

      // --- Hover Logic --- (moved clearing hover on disable/idle up)
      if (!isGrabbing) {
        const remoteHoverTarget = this.intersection && findRemoteHoverTarget(APP.world, this.intersection.object);
        isHoveringSomething = !!remoteHoverTarget;
        if (remoteHoverTarget) {
          addComponent(APP.world, left ? HoveredRemoteLeft : HoveredRemoteRight, remoteHoverTarget);

          const alreadyHovered = hoveredQuery(APP.world);
          if (alreadyHovered.length > 0 && alreadyHovered[0] !== remoteHoverTarget) {
            removeComponent(APP.world, left ? HoveredRemoteLeft : HoveredRemoteRight, alreadyHovered[0]);
            removeComponent(APP.world, Hovered, alreadyHovered[0]);
          }
          addComponent(APP.world, Hovered, remoteHoverTarget);
        }
        const hovered = left ? hoveredLeftRemoteQuery(APP.world) : hoveredRightRemoteQuery(APP.world);
        for (let i = 0; i < hovered.length; i++) {
          // Unhover anything that should no longer be hovered
          if (remoteHoverTarget !== hovered[i]) {
            removeComponent(APP.world, left ? HoveredRemoteLeft : HoveredRemoteRight, hovered[i]);
            removeComponent(APP.world, Hovered, hovered[i]);
          }
        }
        this.distance = remoteHoverTarget ? this.intersection.distance : this.data.defaultDistance * playerScale;
      }

      const { cursor, minDistance, far, camera } = this.data;

      const cursorModDelta =
        userinput.get(left ? paths.actions.cursor.left.modDelta : paths.actions.cursor.right.modDelta) || 0;
      if (isGrabbing && !userinput.activeSets.includes(left ? sets.leftCursorHoldingUI : sets.rightCursorHoldingUI)) {
        this.distance = THREE.MathUtils.clamp(this.distance - cursorModDelta, minDistance, far * playerScale);
      }
      cursor.object3D.position.copy(cursorPose.position).addScaledVector(cursorPose.direction, this.distance);

      // The cursor will always be oriented towards the player about its Y axis...
      getLastWorldPosition(camera.object3D, cameraPos);
      cameraPos.y = cursor.object3D.position.y;
      cursor.object3D.lookAt(cameraPos);
      cursor.object3D.matrixNeedsUpdate = true;

      // TODO : Check if the selected object being transformed is for this cursor!
      const transformObjectSystem = AFRAME.scenes[0].systems["transform-selected-object"];
      if (
        transformObjectSystem.transforming &&
        ((left && transformObjectSystem.hand.el.id === "player-left-controller") ||
          (!left && transformObjectSystem.hand.el.id === "player-right-controller"))
      ) {
        this.color.copy(TRANSFORM_COLOR_1).lerpHSL(TRANSFORM_COLOR_2, 0.5 + 0.5 * Math.sin(t / 1000.0));
      } else if (isGrabbing || isHoveringSomething) {
        this.color.copy(HIGHLIGHT);
      } else {
        this.color.copy(NO_HIGHLIGHT);
      }

      // Line update (only if visible)
      if (this.line.material.visible) {
        const posePosition = cursorPose.position;
        const cursorPosition = cursor.object3D.position;
        const positionArray = this.line.geometry.attributes.position.array;

        positionArray[0] = posePosition.x;
        positionArray[1] = posePosition.y;
        positionArray[2] = posePosition.z;
        positionArray[3] = cursorPosition.x;
        positionArray[4] = cursorPosition.y;
        positionArray[5] = cursorPosition.z;

        this.line.geometry.attributes.position.needsUpdate = true;
        this.line.geometry.computeBoundingSphere();
      }
    };
  })()
});
